import { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AddLifeEventModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    event_type: 'work',
    title: '',
    description: '',
    event_date: '',
    end_date: '',
    all_day: true
  });
  const [loading, setLoading] = useState(false);

  const eventTypes = [
    { value: 'work', label: 'Work' },
    { value: 'social', label: 'Social' },
    { value: 'personal', label: 'Personal' },
    { value: 'other', label: 'Other' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.event_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/study-buddy/life-events`,
        null,
        {
          params: formData,
          withCredentials: true
        }
      );
      
      toast.success('Life event added successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error adding life event:', error);
      toast.error(error.response?.data?.detail || 'Failed to add life event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-[#F4E4C1] to-white rounded-lg max-w-lg w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-[#E85D4D]"
        >
          <X className="w-6 h-6" />
        </button>

        <h2 className="text-2xl font-bold text-black mb-2">Add Life Event</h2>
        <p className="text-sm text-black mb-6">Add work, social, or personal events to your calendar</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-black mb-2">
              Event Type *
            </label>
            <select
              value={formData.event_type}
              onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
              className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded-lg focus:outline-none focus:border-[#E8934D] text-black bg-white"
              required
            >
              {eventTypes.map(type => (
                <option key={type.value} value={type.value} className="text-black">{type.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold text-black mb-2">
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded-lg focus:outline-none focus:border-[#E8934D] text-black bg-white"
              placeholder="e.g., Team Meeting, Birthday Party"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-bold text-black mb-2">
                Start Date *
              </label>
              <input
                type="datetime-local"
                value={formData.event_date}
                onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded-lg focus:outline-none focus:border-[#E8934D] text-black bg-white"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-black mb-2">
                End Date (Optional)
              </label>
              <input
                type="datetime-local"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded-lg focus:outline-none focus:border-[#E8934D] text-black bg-white"
              />
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.all_day}
                onChange={(e) => setFormData({ ...formData, all_day: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm font-bold text-black">All Day Event</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-bold text-black mb-2">
              Description (Optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 border-2 border-[#E8B67A] rounded-lg focus:outline-none focus:border-[#E8934D] h-24 resize-none text-black bg-white"
              placeholder="Add any additional details..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-[#E8B67A] text-black font-bold rounded-lg hover:bg-[#E8B67A]/20 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-[#E8934D] text-white font-bold rounded-lg hover:bg-[#E85D4D] transition-colors disabled:opacity-50"
            >
              {loading ? 'Adding...' : 'Add Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
